import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Search, ExternalLink, Upload, Save, X, ShieldCheck } from 'lucide-react';
import { lookupVmdAffirmation, bulkCreateFellingAreas, importFellingAreas } from '../../services/api';
import { featuresOf, geojsonAreaHa, VMD_API_URL, VMD_TOOL_URL } from '../../lib/geo';

const PropertiesTable = ({ features }) => {
  const first = features[0]?.properties || {};
  const keys = Object.keys(first).filter((k) => first[k] !== null && typeof first[k] !== 'object').slice(0, 14);
  if (keys.length === 0) return <p className="text-xs text-[#2E2E2E]/60">Failā nav aprakstošu atribūtu.</p>;
  return (
    <table className="w-full text-xs" data-testid="vmd-properties">
      <tbody>
        {keys.map((key) => (
          <tr key={key} className="border-t border-gray-100">
            <td className="py-1 pr-2 text-[#2E2E2E]/60 whitespace-nowrap">{key}</td>
            <td className="py-1 font-medium break-all">{String(first[key])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const VmdAffirmationLookup = ({ projectId, onPreview, onSaved, onClose }) => {
  const [number, setNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const applyResult = (geojson, source) => {
    const features = featuresOf(geojson);
    if (features.length === 0) {
      setError('VMD atbildē nav cirsmu ģeometriju.');
      return;
    }
    setResult({ geojson, features, area: geojsonAreaHa(geojson), source });
    setError('');
    onPreview?.(geojson);
  };

  const lookup = async () => {
    const value = number.trim();
    if (!value) return;
    setLoading(true);
    setError('');
    setResult(null);
    onPreview?.(null);
    try {
      const data = await lookupVmdAffirmation(value);
      applyResult(data.geojson, 'serveris');
    } catch (serverError) {
      const serverMsg = serverError.response?.data?.detail || 'Serveris nevar sasniegt VMD.';
      if (serverError.response?.status === 404) {
        setError(serverMsg);
      } else {
        try {
          const response = await fetch(`${VMD_API_URL}${encodeURIComponent(value)}`, { mode: 'cors' });
          if (response.status === 404) throw new Error(`VMD nav atrasts apliecinājums Nr. ${value}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          applyResult(await response.json(), 'pārlūks');
        } catch (browserError) {
          setError(`${serverMsg} Pārlūka pieprasījums: ${browserError.message}. Lejupielādējiet GeoJSON no VMD rīka un importējiet to zemāk.`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const saveAreas = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const created = await bulkCreateFellingAreas(projectId, { geojson: result.geojson, source: 'vmd', affirmation_number: number.trim() || null, name_prefix: `Cirsma ${number.trim()}` });
      toast.success(`Saglabātas ${created.length} cirsmas no apliecinājuma ${number.trim()}`);
      setResult(null);
      onPreview?.(null);
      onSaved?.(created);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Neizdevās saglabāt cirsmas');
    } finally {
      setSaving(false);
    }
  };

  const importFile = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      let created;
      if (/\.(geojson|json)$/i.test(file.name)) {
        const geojson = JSON.parse(await file.text());
        created = await bulkCreateFellingAreas(projectId, { geojson, source: 'vmd', affirmation_number: number.trim() || null, name_prefix: number.trim() ? `Cirsma ${number.trim()}` : 'Cirsma' });
      } else {
        created = await importFellingAreas(projectId, file);
      }
      toast.success(`Importētas ${created.length} cirsmas`);
      onSaved?.(created);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Neizdevās importēt failu');
    } finally {
      setSaving(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 space-y-3 animate-fade-in" data-testid="vmd-lookup">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-blue-900 flex items-center gap-2 text-sm"><ShieldCheck className="w-4 h-4" /> VMD ciršanas apliecinājums</p>
          <p className="text-xs text-blue-900/70">Ievadiet apliecinājuma numuru — cirsmas tiks ielādētas no epak.vmd.gov.lv un parādītas kartē.</p>
        </div>
        <button type="button" onClick={() => { onPreview?.(null); onClose?.(); }} className="text-blue-900/60 hover:text-blue-900" data-testid="vmd-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input value={number} onChange={(e) => setNumber(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && lookup()} placeholder="Piem.: 693126465" className="bg-white" data-testid="vmd-number" />
        <Button onClick={lookup} disabled={loading || !number.trim()} className="bg-blue-700 hover:bg-blue-800 shrink-0" data-testid="vmd-search">
          <Search className="w-4 h-4 mr-2" /> {loading ? 'Meklē...' : 'Meklēt'}
        </Button>
      </div>
      {error && (
        <div className="rounded-lg bg-white border border-blue-200 p-3 space-y-2" data-testid="vmd-error">
          <p className="text-xs text-red-700 whitespace-pre-line">{error}</p>
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline" data-testid="vmd-open-tool">
              <a href={VMD_TOOL_URL} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Atvērt VMD rīku</a>
            </Button>
            <Button asChild size="sm" variant="outline" data-testid="vmd-open-direct">
              <a href={`${VMD_API_URL}${encodeURIComponent(number.trim())}`} target="_blank" rel="noreferrer"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Lejupielādēt GeoJSON</a>
            </Button>
            <input ref={fileRef} type="file" accept=".geojson,.json,.kml,.kmz" className="hidden" onChange={(e) => importFile(e.target.files?.[0])} data-testid="vmd-import-input" />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={saving} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="vmd-import">
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Importēt lejupielādēto failu
            </Button>
          </div>
        </div>
      )}
      {result && (
        <div className="rounded-lg bg-white border border-blue-200 p-3 space-y-2" data-testid="vmd-result">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-blue-900" data-testid="vmd-result-summary">
              Atrastas {result.features.length} cirsmas{result.area ? ` · kopā ${result.area} ha` : ''} <span className="text-xs text-blue-900/60">({result.source})</span>
            </p>
            <Button size="sm" onClick={saveAreas} disabled={saving} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="vmd-save">
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Saglabā...' : 'Saglabāt kā objekta cirsmas'}
            </Button>
          </div>
          <PropertiesTable features={result.features} />
        </div>
      )}
    </div>
  );
};

export default VmdAffirmationLookup;
